import React from "react";
import { ActionIcon, Box, Button, Card, Flex, Modal, Text, Tooltip } from "@mantine/core";
import { DeckBimViewer } from "@multiconsult-group/multiplacer-ui";
import { useState } from "react";
import { FaBrain, FaExpand } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectDevicesExistInRoom } from "../../DesignTab/devices/deviceSlice";
import { thunkAddMlDevicesToSelectedRoom } from "../../DesignTab/devices/deviceThunks";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";
import {
  selectCeilingAiPredictionCeilingGridPixPaths,
  selectCeilingAiPredictionExtentKey,
  selectCeilingAiPredictionViewerPixPolygons,
} from "./ceilingAiPredictionsSelectors";

const CeilingAiPredictionsViewerContent: React.FC<{ openOnClick?: () => void }> = ({
  openOnClick,
}) => {
  const dispatch = useAppDispatch();
  const room = useAppSelector(selectHttpSingleRoomFull);
  const predictionCount = room?.mlPredictedDevices?.length ?? 0;
  const isLocked = room?.multiplacerCeilingDevicesLocked?.bool === true;
  const devicesExistInRoom = useAppSelector(selectDevicesExistInRoom);
  const useAiSuggestionsDisabled = isLocked || devicesExistInRoom;
  const pixPolygons = useAppSelector(selectCeilingAiPredictionViewerPixPolygons);
  const pixPaths = useAppSelector(selectCeilingAiPredictionCeilingGridPixPaths);
  const extentKey = useAppSelector(selectCeilingAiPredictionExtentKey);

  if (!room) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">
          Velg et rom for å se AI-prediksjoner.
        </Text>
      </Box>
    );
  }

  return (
    <Box h="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Flex align="center" justify="space-between" px="sm" py={6}>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="sm">
            AI-prediksjoner
          </Text>
          <Text size="xs" c="dimmed">
            {predictionCount} predikerte komponenter for himlingen.
          </Text>
        </Box>
        <Tooltip
          label={
            devicesExistInRoom
              ? "Rommet har allerede komponenter"
              : "Legg inn komponenter fra AI-forslag"
          }
        >
          <Button
            size="xs"
            variant="light"
            color="blue"
            leftSection={<FaBrain size={14} />}
            disabled={useAiSuggestionsDisabled}
            radius="md"
            onClick={() => dispatch(thunkAddMlDevicesToSelectedRoom())}
          >
            Bruk AI-forslag
          </Button>
        </Tooltip>
      </Flex>

      <Box
        h="100%"
        bg="#f8fafc"
        style={{
          overflow: "hidden",
          position: "relative",
          minHeight: 0,
        }}
      >
        {openOnClick && (
          <Tooltip label="Åpne i fullskjerm" withArrow>
            <ActionIcon
              variant="white"
              color="gray"
              radius="xl"
              size="sm"
              aria-label="Åpne AI-prediksjoner i fullskjerm"
              onClick={openOnClick}
              style={{
                position: "absolute",
                top: 8,
                right: 10,
                zIndex: 2,
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.18)",
              }}
            >
              <FaExpand size={11} />
            </ActionIcon>
          </Tooltip>
        )}

        <DeckBimViewer
          flip2dCoordinate={true}
          angle={180}
          polygons={pixPolygons}
          paths={pixPaths}
          dimensionLines={[]}
          zoomExtentKey={extentKey}
        />
      </Box>
    </Box>
  );
};

export const CeilingAiPredictionsPanel: React.FC = () => {
  const [opened, setOpened] = useState(false);

  return (
    <Card
      withBorder
      radius="md"
      h="100%"
      p={0}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <CeilingAiPredictionsViewerContent openOnClick={() => setOpened(true)} />

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        fullScreen
        padding={0}
        withCloseButton
        title="AI-prediksjoner"
      >
        <Box h="100vh" style={{ minHeight: 0 }}>
          <CeilingAiPredictionsViewerContent />
        </Box>
      </Modal>
    </Card>
  );
};
